import {
  NOTE_PREVIEW_PHARMACY_TITLES,
  NOTE_PREVIEW_FREE_TEXT_TITLES,
  NOTE_PREVIEW_HIDDEN_TABLE_COLUMNS,
  NOTE_PREVIEW_LABEL_VALUE_PATTERN,
  NOTE_PREVIEW_LEADING_ANSWER_PATTERN,
  NOTE_PREVIEW_LEADING_NOTE_MARKER,
  NOTE_PREVIEW_INLINE_LABEL_SEPARATOR,
  NOTE_PREVIEW_NOTE_MARKER,
  NOTE_PREVIEW_NUMBER_PREFIX_PATTERN,
  NOTE_PREVIEW_NUMBERED_ENTRY_PATTERN,
  NOTE_PREVIEW_PROSE_MIN_WORDS,
  NOTE_PREVIEW_SENTENCE_TERMINATOR,
  NOTE_PREVIEW_TRAILING_DOT,
  NOTE_PREVIEW_TRAILING_SEPARATORS,
} from "./note-preview-constant";
import { splitIntoSentences, splitOnCommas } from "./note-preview-split-utils";
import {
  splitByStatementCatalog,
  startsWithFormOption,
} from "./note-preview-statement-utils";
import {
  EncounterSummaryReportTable,
  GroupLinesOptions,
  NotePreviewLine,
  NotePreviewLineGroup,
  NotePreviewTable,
} from "./note-preview-types";

const isVisibleColumn = (column: string) =>
  !(NOTE_PREVIEW_HIDDEN_TABLE_COLUMNS as readonly string[]).includes(
    column.trim()
  );

/** Keeps a table only when it has visible columns and at least one row; cells are padded to the column count. */
export const toPreviewTable = (
  table?: EncounterSummaryReportTable | null
): NotePreviewTable | undefined => {
  const allColumns = (table?.columns ?? []).map((column) => column ?? "");
  const visibleIndexes = allColumns.flatMap((column, index) =>
    isVisibleColumn(column) ? [index] : []
  );
  const columns = visibleIndexes.map((index) => allColumns[index]);
  const rows = (table?.rows ?? []).map((row) =>
    visibleIndexes.map((index) => row[index] ?? "")
  );
  return columns.length && rows.length ? { columns, rows } : undefined;
};

interface SplitParts {
  items: string[];
  paragraphs: string[];
}

const wordCount = (sentence: string) => sentence.trim().split(/\s+/).length;

const isLongSentence = (sentence: string) =>
  wordCount(sentence) >= NOTE_PREVIEW_PROSE_MIN_WORDS;

/**
 * Statements that start long ("The patient is being referred…") are one bullet
 * each; a short selection ("yes") followed by long sentences is a selection
 * with its explanation as a paragraph. Short sentences split on commas.
 */
const splitSelections = (text: string): SplitParts => {
  const glued = startsWithFormOption(text)
    ? null
    : text.match(NOTE_PREVIEW_LEADING_ANSWER_PATTERN);
  if (glued) {
    const rest = splitSelections(text.slice(glued[0].length));
    const explanation = rest.paragraphs.length
      ? rest.paragraphs
      : [rest.items.join(`${NOTE_PREVIEW_SENTENCE_TERMINATOR} `)];
    return { items: [glued[1]], paragraphs: explanation };
  }
  const statements = splitByStatementCatalog(text);
  if (statements) return { items: statements, paragraphs: [] };
  const sentences = splitIntoSentences(text);
  if (sentences.length && isLongSentence(sentences[0])) {
    return {
      items: sentences.flatMap((sentence) =>
        isLongSentence(sentence) ? [sentence] : splitOnCommas(sentence)
      ),
      paragraphs: [],
    };
  }
  const proseStart = sentences.findIndex(isLongSentence);
  const selections =
    proseStart === -1 ? sentences : sentences.slice(0, proseStart);
  const prose = proseStart === -1 ? [] : sentences.slice(proseStart);
  return {
    items: selections.flatMap((sentence) => splitOnCommas(sentence)),
    paragraphs: prose.length
      ? [
          `${prose.join(`${NOTE_PREVIEW_SENTENCE_TERMINATOR} `)}${NOTE_PREVIEW_SENTENCE_TERMINATOR}`,
        ]
      : [],
  };
};

/**
 * Splits a checkbox answer such as "WNL, Obese, Allergy (eeee)" or
 * "Smoker: never. Alcohol: none" into one bullet per selection. Separators
 * inside brackets stay with their item, a trailing "Note: …" is one bullet
 * however it is punctuated, and explanatory sentences become paragraphs.
 */
export const splitIntoParts = (text: string): SplitParts => {
  const noteIndex = text.indexOf(NOTE_PREVIEW_NOTE_MARKER);
  if (noteIndex === -1) return splitSelections(text);
  const note = text.slice(noteIndex);
  const parts = splitSelections(
    text.slice(0, noteIndex).replace(NOTE_PREVIEW_TRAILING_SEPARATORS, "")
  );
  return { ...parts, items: [...parts.items, note.trim()] };
};

/** Bullets only; see `splitIntoParts` for the paragraph split. */
export const splitIntoBullets = (text: string): string[] =>
  splitIntoParts(text).items;

/**
 * The free-text label a title carries, if any. The backend sometimes prefixes
 * it with the last list item ("Z993 - Dependence on wheelchair Additional note"),
 * so a title that ends with the label counts too.
 */
export const freeTextLabelOf = (title: string): string | undefined => {
  const lower = title.trim().toLowerCase();
  return (NOTE_PREVIEW_FREE_TEXT_TITLES as readonly string[]).find(
    (label) =>
      lower === label.toLowerCase() || lower.endsWith(` ${label.toLowerCase()}`)
  );
};

export const isFreeTextTitle = (title: string) => !!freeTextLabelOf(title);

const whole = (text: string): SplitParts => ({ items: [text], paragraphs: [] });

export const isPharmacyTitle = (title: string) =>
  (NOTE_PREVIEW_PHARMACY_TITLES as readonly string[]).some(
    (label) => label.toLowerCase() === title.trim().toLowerCase()
  );

/** Titled lines split fully, except free-text titles; untitled "Label: value" row lists split too; other prose stays whole. */
const toParts = (
  { title, text }: NotePreviewLine,
  options: GroupLinesOptions
): SplitParts => {
  if (title && isFreeTextTitle(title))
    return whole(text.replace(NOTE_PREVIEW_LEADING_NOTE_MARKER, ""));
  if (title && options.keepCommas)
    return whole(text.replace(NOTE_PREVIEW_TRAILING_DOT, ""));
  if (title && isPharmacyTitle(title))
    return { items: splitIntoSentences(text), paragraphs: [] };
  if (title) return splitIntoParts(text);
  if (!NOTE_PREVIEW_LABEL_VALUE_PATTERN.test(text)) return whole(text);
  const items = splitIntoBullets(text);
  return items.length > 1 ? { items, paragraphs: [] } : whole(text);
};

const isNumberedEntry = (line: NotePreviewLine) =>
  !!line.title &&
  !line.isNested &&
  NOTE_PREVIEW_NUMBERED_ENTRY_PATTERN.test(line.title);

/** "1. Condition" + "rgregre" prints as "1. Condition: rgregre", keeping the backend's label. */
const entryTitle = (line: NotePreviewLine): string => {
  const title = line.title ?? "";
  const number = title.match(NOTE_PREVIEW_NUMBERED_ENTRY_PATTERN)?.[1] ?? "";
  const label = title.replace(NOTE_PREVIEW_NUMBER_PREFIX_PATTERN, "").trim();
  return `${number}. ${label}${NOTE_PREVIEW_INLINE_LABEL_SEPARATOR}${line.text}`;
};

/** A nested detail under a numbered entry prints as "Label: text", e.g. "Severity: Mild" or "Notes: …". */
const nestedItem = ({ title, text }: NotePreviewLine): string =>
  title ? `${title}${NOTE_PREVIEW_INLINE_LABEL_SEPARATOR}${text}` : text;

/**
 * Merges consecutive lines with the same title into one bulleted block. Titled
 * lines are checkbox groups, so their text splits into one bullet per selection;
 * untitled lines are free text and stay whole. A numbered chart entry such as
 * "1. Condition" becomes "1. <value>" with its nested lines as bullets.
 */
export const groupSectionLines = (
  lines: NotePreviewLine[],
  options: GroupLinesOptions = {}
): NotePreviewLineGroup[] =>
  lines.reduce<NotePreviewLineGroup[]>((groups, line) => {
    const last = groups[groups.length - 1];
    if (isNumberedEntry(line)) {
      groups.push({
        title: entryTitle(line),
        isNested: false,
        items: [],
        paragraphs: [],
        isBulleted: true,
        isInline: false,
        isEntry: true,
      });
      return groups;
    }
    if (last?.isEntry && line.isNested) {
      last.items.push(nestedItem(line));
      return groups;
    }
    const { items, paragraphs } = toParts(line, options);
    const isInline = !!line.title && isFreeTextTitle(line.title);
    if (
      last &&
      line.title &&
      !isInline &&
      last.title === line.title &&
      last.isNested === line.isNested
    ) {
      last.items.push(...items);
      last.paragraphs.push(...paragraphs);
      last.isBulleted = true;
      return groups;
    }
    groups.push({
      title: isInline ? undefined : line.title,
      isNested: line.isNested,
      items,
      paragraphs,
      isBulleted: !isInline && (!!line.title || items.length > 1),
      isInline,
      isEntry: false,
    });
    return groups;
  }, []);

export { splitIntoSentences } from "./note-preview-split-utils";
